import { getConfig } from '../config';
import { trackError } from '../error/tracking';
import { captureException } from '../logger/capture-exception';

declare global {
  interface Window {
    Beacon?: ((method: string, options?: unknown, data?: unknown) => void) & {
      readyQueue?: Array<{ method: string; options?: unknown; data?: unknown }>;
    };
  }
}

const BEACON_SCRIPT_URL = 'https://beacon-v2.helpscout.net';
const BEACON_SCRIPT_ID = 'helpscout-beacon';

function delay(ms: number) {
  return new Promise(function (resolve) {
    setTimeout(resolve, ms);
  });
}

async function retryOperation(
  operation: () => unknown,
  maxAttempts = 5,
  delayMs = 1000
): Promise<void> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (typeof window.Beacon === 'function') {
      operation();
      return;
    }
    await delay(delayMs);
  }
}

function loadBeaconScript() {
  if (document.querySelector('#' + BEACON_SCRIPT_ID) != null) {
    return;
  }

  if (typeof window.Beacon !== 'function') {
    const beaconStub = function (method: string, options?: unknown, data?: unknown) {
      if (window.Beacon?.readyQueue != null) {
        window.Beacon.readyQueue.push({ method: method, options: options, data: data });
      }
    } as NonNullable<Window['Beacon']>;
    beaconStub.readyQueue = [];
    window.Beacon = beaconStub;
  }

  function insertScript() {
    const firstScript = document.querySelectorAll('script')[0];
    const script = document.createElement('script');
    script.id = BEACON_SCRIPT_ID;
    script.type = 'text/javascript';
    script.async = true;
    script.src = BEACON_SCRIPT_URL;
    if (firstScript?.parentNode != null) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  }

  if (document.readyState === 'complete') {
    insertScript();
  } else {
    window.addEventListener('load', insertScript, false);
  }
}

export function initHelpScout() {
  const beaconId = getConfig().helpScoutBeaconId;
  if (beaconId === '') {
    return;
  }

  loadBeaconScript();
  if (typeof window.Beacon === 'function') {
    window.Beacon('init', beaconId);
  }
}

export async function alignHelpScoutIcon(path: string) {
  const isContentPath = path.startsWith('/app/content/');

  try {
    await retryOperation(function () {
      if (typeof window.Beacon === 'function') {
        window.Beacon('config', {
          display: {
            position: isContentPath ? 'left' : 'right',
          },
        });
      }
    });
  } catch (err) {
    captureException(
      err,
      {
        file: 'services/helpscout.ts',
        method: 'alignHelpScoutIcon',
        infoMessage: 'Failed to align HelpScout Beacon after max attempts',
      },
      trackError
    );
  }
}

export async function openHelpScoutChat() {
  try {
    await retryOperation(function () {
      if (typeof window.Beacon === 'function') {
        window.Beacon('open');
      }
    });
  } catch (err) {
    captureException(
      err,
      {
        file: 'services/helpscout.ts',
        method: 'openHelpScoutChat',
        infoMessage: 'Failed to open HelpScout Beacon after max attempts',
      },
      trackError
    );
  }
}
